import type {ExpenseListResource, ExpenseResource} from '@api';
import {PENDING_EXPENSE_GROUP_ID, type FormattedExpenseBatchGroup, type FormattedExpenseBatchRow} from '../types';

export function allExpensesOf(expenseList: ExpenseListResource | null | undefined): ExpenseResource[] {
    return [...(expenseList?.expenses ?? []), ...(expenseList?.batches ?? []).flatMap((batch) => batch.expenses)];
}

export function batchIdsByExpenseId(expenseList: ExpenseListResource | null | undefined): Map<string, string> {
    return new Map(
        (expenseList?.batches ?? []).flatMap((batch) => batch.expenses.map((expense) => [expense.expenseId, batch.expenseBatchId] as const)),
    );
}

export function groupRanks(batchGroups: FormattedExpenseBatchGroup[]): Map<string, number> {
    const newestFirst = [...batchGroups].sort((a, b) => (b.rawCreatedDate?.getTime() ?? 0) - (a.rawCreatedDate?.getTime() ?? 0));
    return new Map([PENDING_EXPENSE_GROUP_ID, ...newestFirst.map((group) => group.id)].map((id, rank) => [id, rank]));
}

export function withBatchRows<T extends {groupId: string}>(
    groupedExpenses: T[],
    batchGroupsById: Map<string, FormattedExpenseBatchGroup>,
    expandedGroupIds: ReadonlySet<string>,
): (T | FormattedExpenseBatchRow)[] {
    return groupedExpenses.flatMap((expense, index) => {
        const batch = batchGroupsById.get(expense.groupId);
        if (!batch) {
            return [expense];
        }
        const expanded = expandedGroupIds.has(batch.id);
        const visibleExpense = expanded ? [expense] : [];
        const startsBatch = groupedExpenses[index - 1]?.groupId !== expense.groupId;
        return startsBatch ? [{id: batch.id, groupId: batch.id, batch, expanded}, ...visibleExpense] : visibleExpense;
    });
}
